import React, { useState, useEffect } from 'react';
import Countdown from 'react-countdown';
import DuoIcon from '../../../assets/icons/dashboard/programs/programCard/duoIcon.svg';
import QuattroIcon from '../../../assets/icons/dashboard/programs/programCard/quattroIcon.svg';
import { PROGRAM_NAMES, PROGRAM_PRICES_WEI } from '../../../helpers/constants';
import { useTranslation } from 'react-i18next';
import EnergyIcon from '../../../assets/icons/passive/energy.svg';
import PolIcon from '../../../assets/icons/main/polCoin.svg';
import PollyIcon from '../../../assets/icons/main/pollyCoin.svg';
import CheckIcon from '../../../assets/icons/passive/checkIcon.svg';
import { Button } from '../../../components/Button';
import { ActivateModal } from '../../../components/Modals/ActivateModal';
import { usePassiveIncome } from '../../../helpers/hooks/usePassiveIncome';
import { useCallTransaction } from '../../../helpers/hooks/useCallTransaction';
import { useModal } from '../../../helpers/hooks/useModal';
import { bigIntToInt } from '../../../helpers/numbers';
import { useSelector } from 'react-redux';
import { getUser } from '../../../store/userSlice/selectors';
import { useWeb3React } from '@web3-react/core';
import { Pulse } from '../Pulse';
import { getConsecutiveActiveLevel } from '../../../helpers/program';

export const ProgramCard = ({ isLoading = false, type, passiveData, updatePassiveData, maxActiveLevel = null }) => {
  const { t } = useTranslation();
  const { account } = useWeb3React();
  const { openedModal, onOpen, onClose } = useModal();
  const isLoggedIn = !!account;
  const { activatePassive, claimPassive, isLoadingActivation, isLoadingClaim } = usePassiveIncome();
  const {
    onCallTransaction: onCallTransactionClaim,
    transactionInfo: transactionInfoClaim,
    resetTransactionInfo: resetTransactionInfoClaim,
  } = useCallTransaction();
  const {
    onCallTransaction: onCallTransactionBoost,
    transactionInfo: transactionInfoBoost,
    resetTransactionInfo: resetTransactionInfoBoost,
  } = useCallTransaction();

  const releaseDate = new Date('2025-11-17T16:00:00Z');
  const [isTimerCompleted, setIsTimerCompleted] = useState(Date.now() >= releaseDate.getTime());

  useEffect(() => {
    if (transactionInfoClaim.isSuccess) {
      resetTransactionInfoClaim();
      updatePassiveData();
    }
    if (transactionInfoBoost.isSuccess) {
      resetTransactionInfoBoost();
      updatePassiveData();
    }
  }, [transactionInfoClaim, transactionInfoBoost]);

  const activatePassiveClick = async () => {
    try {
      const result = await activatePassive(type, passiveData?.lastActiveLevel + 1);
      if (result) {
        onCallTransactionBoost(result);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const claimPassiveClick = async () => {
    try {
      const result = await claimPassive(passiveData?.contractAddress);
      if (result) {
        onCallTransactionClaim(result);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const currentUser = useSelector(getUser);

  const userMaxActiveLevel = React.useMemo(() => {
    if (maxActiveLevel !== null && maxActiveLevel !== undefined) {
      return maxActiveLevel;
    }
    const userLevels = type === PROGRAM_NAMES.X2 ? currentUser?.levelsX2 : currentUser?.levelsX4;
    if (!userLevels) return 0;
    return getConsecutiveActiveLevel(userLevels);
  }, [currentUser, maxActiveLevel, type]);

  const nextLevelToActivate = passiveData?.lastActiveLevel + 1;
  const isLevelMaxed = userMaxActiveLevel < nextLevelToActivate;
  const isAllLevelsBoosted = userMaxActiveLevel === 15 && passiveData?.lastActiveLevel === 15;

  const timerRenderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      setIsTimerCompleted(true);
      return null;
    }
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return (
      <span className="text-white text-[1.6rem] font-500">
        {days > 0 ? `${days}d ` : ''}
        {formattedHours}:{formattedMinutes}:{formattedSeconds}
      </span>
    );
  };

  const handleTimerComplete = () => {
    setIsTimerCompleted(true);
  };

  const isLoadedLastActiveLevel = passiveData?.lastActiveLevel !== null && passiveData?.lastActiveLevel !== undefined;

  return (
    <div className="flex flex-col space-y-[3.2rem] w-full border-[0.1rem] border-solid border-white-50 rounded-[3.2rem] p-[2rem] bg-black-300 lg:bg-white-30 lg:rounded-[2rem]">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center space-x-[1.2rem] ">
          <img className="w-[2.8rem] h-[2.8rem]" src={type === PROGRAM_NAMES.X2 ? DuoIcon : QuattroIcon} />
          <span className="text-[1.8rem] font-600 text-white lg:text-[1.6rem]">
            {t('dashboard.programs.card.title', { program: type })}
          </span>
        </div>
        {isLoggedIn ? (
          <div className="flex items-center space-x-[1rem] h-[2.8rem] rounded-[2rem] border-[0.1rem] border-solid border-[#985AFF] bg-[rgba(152,90,255,0.10)]  pl-[0.8rem] pr-[1.2rem]">
            <img src={EnergyIcon} />
            {isLoading ? (
              <Pulse className="h-[1.4rem] w-[7rem]" />
            ) : (
              <span className="text-[1.4rem] font-500 text-white tracking-[-0.28px]">
                {passiveData?.lastActiveLevel} {t('passive.programs.boosted')}
              </span>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onOpen}
            className="flex items-center space-x-[1rem] h-[2.8rem] rounded-[2rem] border-[0.1rem] border-solid border-[#985AFF] bg-[rgba(152,90,255,0.10)] pl-[0.8rem] pr-[1.2rem] custom-transition hover:opacity-80"
          >
            <img src={EnergyIcon} alt="" />
            <span className="text-[1.4rem] font-500 text-white tracking-[-0.28px]">
              Login to view {t('passive.programs.boosted')}
            </span>
          </button>
        )}
      </div>
      <div className="flex flex-col space-y-[1.6rem] w-full">
        <span className="text-white-400 text-[1.6rem] ">Profit</span>
        {isLoggedIn ? (
          <div className="flex items-center space-x-[0.8rem]">
            {isLoading ? (
              <Pulse className="h-[3.2rem] w-[10rem]" />
            ) : (
              <span className="text-[3.2rem] text-white font-600 leading-[7.68px] lg:text-[2.8ren] lg:leading-[6.72px">
                {(passiveData?.passiveData?.totalClaimed || 0).toFixed(0)}
              </span>
            )}
            <img className="w-[2rem] h-[2rem]" src={PollyIcon} />
          </div>
        ) : (
          <button
            type="button"
            onClick={onOpen}
            className="text-[2rem] font-600 text-white-500 text-left custom-transition hover:text-white"
          >
            Login to view Profit
          </button>
        )}
      </div>
      <div className="flex flex-col space-y-[1.2rem]">
        <div className="flex flex-col space-y-[1.2rem] w-full rounded-[1.6rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.03)_100%)] shadow-[0_10px_30px_0_rgba(0,0,0,0.30)] backdrop-blur-[0.5rem] lg:bg-[linear-gradient(180deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.03)_100%)]">
          <div className="flex flex-col space-y-[1.2rem] p-[1.6rem]">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-[1.2rem]">
                <img src={CheckIcon} />
                <span className="text-[1.4rem] tracking-[-0.42px] leading-normal text-white font-400">
                  Top-30 Users
                </span>
              </div>
              <div className="flex items-center space-x-[0.6rem]">
                {isLoading ? (
                  <Pulse className="h-[1.6rem] w-[4.8rem]" />
                ) : (
                  <span className="text-white text-[1.4rem] font-600">
                    {(passiveData?.passiveData?.l3 || 0).toFixed(0)}
                  </span>
                )}
                <img className="w-[1.6rem] h-[1.6rem]" src={PollyIcon} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-[1.2rem]">
                <img src={CheckIcon} />
                <span className="text-[1.4rem] tracking-[-0.42px] leading-normal text-white font-400">
                  Active Users
                </span>
              </div>
              <div className="flex items-center space-x-[0.6rem]">
                {isLoading ? (
                  <Pulse className="h-[1.6rem] w-[4.8rem]" />
                ) : (
                  <span className="text-white text-[1.4rem] font-600">
                    {(passiveData?.passiveData?.l1 || 0).toFixed(0)}
                  </span>
                )}
                <img className="w-[1.6rem] h-[1.6rem]" src={PollyIcon} />
              </div>
            </div>
          </div>
          {isLoggedIn ? (
            <Button
              disabled={
                !isTimerCompleted ||
                isLoadingActivation ||
                transactionInfoBoost.isWaiting ||
                !passiveData?.contractAddress ||
                isLevelMaxed ||
                !isLoadedLastActiveLevel
              }
              loading={isLoadingActivation || transactionInfoBoost.isWaiting || !isLoadedLastActiveLevel}
              onClick={activatePassiveClick}
              type="purple"
              loadingType="white"
              className="flex items-center space-x-[0.8rem] h-[4.8rem] py-[0.8rem] font-500 px-[1.6rem] text-[1.6rem] space-x-[0.5rem] !rounded-[1.2rem] border-[0.1rem] border-solid border-white-200 shadow-[0_5px_10px_0_rgba(255,255,255,0.25)_inset,0_10px_40px_0_rgba(152,90,255,0.30)] lg:w-full"
            >
              {!isTimerCompleted ? (
                <Countdown
                  date={releaseDate}
                  renderer={timerRenderer}
                  autoStart
                  onComplete={handleTimerComplete}
                  overtime={false}
                />
              ) : (
                <>
                  <span>
                    {isAllLevelsBoosted
                      ? t('passive.programs.allLevelsBoosted')
                      : isLevelMaxed
                      ? t('passive.programs.activateLevelFirst', { level: nextLevelToActivate })
                      : t('passive.programs.boost')}
                  </span>
                  {!isLevelMaxed && !isAllLevelsBoosted && (
                    <div className="flex items-center space-x-[0.4rem]">
                      <span className="text-white-500 text-[1.6rem] font-500 tracking-[-0.32px]">
                        {t('passive.programs.for')}
                        <span className="text-white">
                          {' '}
                          {bigIntToInt(PROGRAM_PRICES_WEI[type][passiveData?.lastActiveLevel]) * 0.2}
                        </span>
                      </span>
                      <img className="w-[2rem] h-[2rem]" src={PolIcon} />
                    </div>
                  )}
                </>
              )}
            </Button>
          ) : (
            <button
              type="button"
              onClick={onOpen}
              className="flex items-center justify-center w-full h-[4.8rem] py-[0.8rem] px-[1.6rem] text-[1.6rem] font-500 text-white !rounded-[1.2rem] border-[0.1rem] border-solid border-white-200 bg-white-40 custom-transition hover:bg-white-70"
            >
              Login to view {t('passive.programs.boost')}
            </button>
          )}
        </div>
        {isLoggedIn ? (
          <Button
            onClick={claimPassiveClick}
            loading={isLoadingClaim || transactionInfoClaim.isWaiting || !isLoadedLastActiveLevel}
            disabled={
              isLoadingClaim ||
              transactionInfoClaim.isWaiting ||
              !passiveData?.contractAddress ||
              !passiveData?.passiveData?.unclaimed ||
              passiveData?.passiveData?.unclaimed === 0 ||
              !isLoadedLastActiveLevel
            }
            type="border"
            loadingType="white"
          >
            <span className="text-white">{t('passive.programs.claimPassive')}</span>
            <div className="flex items-center space-x-[0.4rem] text-white">
              {isLoading ? (
                <Pulse className="h-[1.6rem] w-[4rem]" />
              ) : (
                <span className="tracking-[-0.32px]"> {(passiveData?.passiveData?.unclaimed || 0).toFixed(0)}</span>
              )}
              <img className="w-[2rem] h-[2rem]" src={PollyIcon} />
            </div>
          </Button>
        ) : (
          <button
            type="button"
            onClick={onOpen}
            className="flex items-center justify-center w-full h-[4.8rem] rounded-[1.2rem] border-[0.1rem] border-solid border-white-200 text-[1.6rem] font-500 text-white custom-transition hover:bg-white-40"
          >
            Login to view {t('passive.programs.claimPassive')}
          </button>
        )}
      </div>
      <ActivateModal openedModal={openedModal} handleCloseModal={onClose} />
    </div>
  );
};
